import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import MainLayout from "../layoutpage/MainLayout";
import SuccessModal from "../../components/SuccessModal";
import Footer from "../../components/Footer";
import { useMemo } from "react";

const UNITS_PER_PAGE = 10;

export default function PropertyUnitsBookingList({ user, onLogout }) {
  const { id } = useParams();
  const [units, setUnits] = useState([]);
  const [unitsPage, setUnitsPage] = useState(1);
  const [unitsLastPage, setUnitsLastPage] = useState(1);
  const [unitsTotal, setUnitsTotal] = useState(0);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [property, setProperty] = useState(null);
  const [owner, setOwner] = useState(null);
  const [manager, setManager] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const navigate = useNavigate();

  user = JSON.parse(localStorage.getItem("user"));

  const fetchOwner = async (ownerId) => {
    try {
      const res = await api.get(`/api/users/${ownerId}`);
      setOwner(res.data);
    } catch (error) {
      console.log("Owner fetch failed", error);
    }
  };

  const fetchManager = async (managerId) => {
    try {
      const res = await api.get(`/api/users/${managerId}`);
      setManager(res.data);
    } catch (error) {
      console.log("Manager fetch failed", error);
    }
  };

  const fetchUnits = useCallback((page = 1) => {
    if (!id) return;
    setUnitsLoading(true);
    api
      .get(`/api/units`, {
        params: { property_id: id, page, per_page: UNITS_PER_PAGE },
      })
      .then((res) => {
        const data = res.data;
        setUnits(Array.isArray(data?.unit) ? data.unit : []);
        setUnitsPage(data?.current_page ?? 1);
        setUnitsLastPage(data?.last_page ?? 1);
        setUnitsTotal(data?.total ?? 0);
      })
      .catch((err) => console.log(err))
      .finally(() => setUnitsLoading(false));
  }, [id]);

  useEffect(() => {
    fetchUnits(1);
  }, [fetchUnits]);

  useEffect(() => {
    if (!id) return;
    api
      .get(`/api/properties/${id}`)
      .then((res) => {
        const data = res.data;
        setProperty(data);
        if (data.owner_id) fetchOwner(data.owner_id);
        if (data.manager_id) fetchManager(data.manager_id);
      })
      .catch((err) => console.log(err));
  }, [id]);

  //  Open Modal
  const openBookingModal = (unit) => {
    setSelectedUnit(unit);
    setShowModal(true);
  };

  //  Close Modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedUnit(null);
  };

  //  Booking API
  const handleBookingSubmit = async () => {
    const today = new Date();
    const checkInDate = today.toISOString().split("T")[0];

    const checkoutDate = new Date(today);
    checkoutDate.setDate(today.getDate() + 3);
    const checkOutDate = checkoutDate.toISOString().split("T")[0];

    try {
      await api.post("/api/bookings", {
        property_id: selectedUnit.property_id,
        unit_id: selectedUnit.id,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
      });

      setShowSuccessModal(true);
      closeModal();
      fetchUnits(unitsPage);
    } catch (error) {
      console.log(error);
      alert(error.response?.data?.message ?? "Booking failed");
    }
  };

  // Photo helper functions
  const getPropertyPrimaryPhoto = (property) => {
    if (!property?.photos || !Array.isArray(property.photos) || property.photos.length === 0) {
      return null;
    }
    const photo = property.photos.find((p) => p.is_primary) || property.photos[0];
    if (photo.file_path.startsWith("http")) {
      return photo.file_path;
    }
    return `${api.defaults.baseURL}/${photo.file_path}`;
  };

  const getAllPropertyPhotos = (property) => {
    if (!property?.photos || !Array.isArray(property.photos)) return [];
    return property.photos.map((photo) => ({
      ...photo,
      url: photo.file_path.startsWith("http")
        ? photo.file_path
        : `${api.defaults.baseURL}/${photo.file_path}`,
    }));
  };

  const getUnitPhotoUrl = (unit) => {
    if (!unit?.photos || !Array.isArray(unit.photos) || unit.photos.length === 0) {
      return null;
    }
    const photo = unit.photos.find((p) => p.is_primary) || unit.photos[0];
    if (photo.file_path.startsWith("http")) {
      return photo.file_path;
    }
    return `${api.defaults.baseURL}/${photo.file_path}`;
  };

  // Memoized photo data
  const primaryPhoto = useMemo(() => getPropertyPrimaryPhoto(property), [property]);
  const allPhotos = useMemo(() => getAllPropertyPhotos(property), [property]);

  return (
    <>
      <div className="container-fluid">
        {/* back button */}
        <button className="btn btn-secondary mb-3" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        {/* Property Details Card */}
        {property && (
          <div className="card shadow-lg border-0 rounded-4 p-3 mb-4">
            <div className="card-header bg-white border-0">
              <h3 className="fw-bold mb-0">{property.name}</h3>
            </div>
            <div className="card-body">
              <div className="row g-4">
                {/* Left Side Info */}
                <div className="col-md-7">
                  <dl className="row mb-0">
                    <dt className="col-sm-4">Type</dt>
                    <dd className="col-sm-8">{property.type?.name || "—"}</dd>

                    <dt className="col-sm-4">Status</dt>
                    <dd className="col-sm-8">
                      {property.status?.status_type || "—"}
                    </dd>

                    <dt className="col-sm-4">Location</dt>
                    <dd className="col-sm-8">
                      {property.location?.area || "NA"} <br />
                      {property.location?.city
                        ? `${property.location.city}`
                        : ""}
                    </dd>

                    <dt className="col-sm-4">Total Floors</dt>
                    <dd className="col-sm-8">{property.total_floors ?? "—"}</dd>

                    <dt className="col-sm-4">Total Units</dt>
                    <dd className="col-sm-8">
                      {property.units_count ?? property.total_units ?? 0}
                    </dd>

                    <dt className="col-sm-4">Area (sq-ft)</dt>
                    <dd className="col-sm-8">{property.area_sq_ft ?? "—"}</dd>

                    <dt className="col-sm-4">Owner</dt>
                    <dd className="col-sm-8">
                      {owner ? (
                        <>
                          <strong>{owner.name}</strong> <br />
                          <small className="text-muted">
                            {owner.email}
                          </small>{" "}
                          <br />
                          <small className="text-muted">
                            {owner.mobile_number ?? ""}
                          </small>
                        </>
                      ) : (
                        "Owner information not found"
                      )}
                    </dd>

                    <dt className="col-sm-4">Manager</dt>
                    <dd className="col-sm-8">
                      {manager ? (
                        <>
                          <strong>{manager.name}</strong> <br />
                          <small className="text-muted">
                            {manager.email}
                          </small>{" "}
                          <br />
                          <small className="text-muted">
                            {manager.mobile_number ?? ""}
                          </small>
                        </>
                      ) : (
                        "Manager information not found"
                      )}
                    </dd>
                  </dl>

                  {property.description && (
                    <>
                      <hr />
                      <span className="fw-bold">Description</span>
                      <p className="mt-2 text-muted">{property.description}</p>
                    </>
                  )}
                </div>

                {/* Right Side Photos */}
                <div className="col-md-5">
                  {allPhotos.length > 0 ? (
                    <div>
                      {/* Primary Photo */}
                      {primaryPhoto && (
                        <div className="mb-3 border rounded overflow-hidden">
                          <img
                            src={primaryPhoto}
                            alt={property.name}
                            className="img-fluid w-100"
                            style={{ maxHeight: 260, objectFit: "cover" }}
                          />
                        </div>
                      )}
                      {/* Photo Gallery */}
                      {allPhotos.length > 1 && (
                        <div className="row g-2">
                          {allPhotos.slice(0, 4).map((photo, index) => (
                            <div key={photo.id} className="col-6">
                              <img
                                src={photo.url}
                                alt={`${property.name} photo ${index + 1}`}
                                className="img-fluid rounded"
                                style={{ height: "80px", width: "100%", objectFit: "cover" }}
                              />
                            </div>
                          ))}
                          {allPhotos.length > 4 && (
                            <div className="col-6 d-flex align-items-center justify-content-center bg-light rounded">
                              <span className="text-muted">+{allPhotos.length - 4} more</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border rounded overflow-hidden bg-light h-100 d-flex align-items-center justify-content-center">
                      <span className="text-muted">No Photos Available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <h2 className="fw-bold mb-4 text-center">Available Units</h2>

        <div className="card shadow-lg border-0 rounded-4 p-3">
          {unitsLoading ? (
            <p className="text-muted p-3 mb-0">Loading units...</p>
          ) : unitsTotal === 0 ? (
            <p className="text-muted p-3 mb-0">No units found for this property.</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="bg-gray text-muted text-center ">
                    <tr>
                      <th>Unit</th>
                      <th>Image</th>
                      <th>Floor</th>
                      <th>Price</th>
                      <th>Owner</th>
                      <th>Manager</th>
                      <th>Type</th>
                      <th>Area</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  <tbody className="text-center">
                    {units
                      .filter((b) => b.status?.status_type === "available")
                      .map((b) => (
                        <tr key={b.id}>
                          <td>{b.unit_number}</td>

                          <td>
                            {getUnitPhotoUrl(b) ? (
                              <img
                                src={getUnitPhotoUrl(b)}
                                alt={b.unit_number}
                                style={{
                                  height: "55px",
                                  width: "70px",
                                  objectFit: "cover",
                                  borderRadius: "6px",
                                }}
                              />
                            ) : (
                              <span className="text-muted">No photo</span>
                            )}
                          </td>

                          <td>{b.floor}</td>
                          <td className="fw-bold text-success">৳ {b.price}</td>

                          <td>{b.owner?.name ?? "N/A"}</td>
                          <td>{b.manager?.name ?? "N/A"}</td>

                          <td>{b.type?.name}</td>

                          <td>
                            {b.measurement?.length > 0
                              ? `${b.measurement[0].area_sq_ft} ${b.measurement[0].unit_of_measure}`
                              : "N/A"}
                          </td>

                          <td className="text-center">
                            <div className="d-flex justify-content-center">
                              {b.bookings?.length > 0 &&
                                user?.id === b.bookings[0].user_id && (
                                  <Link
                                    to={`/properties_available_booking/${b.bookings[0].id}`}
                                    className="btn btn-info btn-sm me-2"
                                  >
                                    Check In
                                  </Link>
                                )}

                              <Link
                                to={`/units/${b.id}`}
                                className="btn btn-primary btn-sm me-2 "
                              >
                                View Details
                              </Link>

                              {!b.bookings?.length && (
                                <button
                                  className="btn btn-success btn-sm px-3"
                                  onClick={() => openBookingModal(b)}
                                >
                                  Book Now
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {unitsTotal > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2 px-3 pb-3">
                  <span className="text-muted small">
                    Showing {unitsPage === 1 ? 1 : (unitsPage - 1) * UNITS_PER_PAGE + 1}–{Math.min(unitsPage * UNITS_PER_PAGE, unitsTotal)} of {unitsTotal} units
                  </span>
                  <div className="d-flex gap-1 align-items-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={unitsPage <= 1 || unitsLoading}
                      onClick={() => fetchUnits(unitsPage - 1)}
                    >
                      Previous
                    </button>
                    <span className="px-2 small">
                      Page {unitsPage} of {unitsLastPage}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={unitsPage >= unitsLastPage || unitsLoading}
                      onClick={() => fetchUnits(unitsPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* MODAL */}
        {showModal && (
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    Booking Unit: {selectedUnit.unit_number}
                  </h5>
                  <button
                    className="btn-close btn-close-white"
                    onClick={closeModal}
                  ></button>
                </div>

                <div className="modal-body">
                  <p className="fs-5">
                    You are going to book the property now. It will be reserved
                    for you until{" "}
                    <strong>
                      {new Date(
                        new Date().setDate(new Date().getDate() + 3)
                      ).toDateString()}
                    </strong>
                    . You must finish paperwork and payment before that.
                  </p>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary px-4"
                    onClick={handleBookingSubmit}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        <SuccessModal
          show={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          message="Booking Successful! Please proceed with payment."
        />
      </div>

      {/* Extra Hover Effect */}
      <style>{`
          .table-hover tbody tr:hover {
            background-color: #f6f9ff !important;
            cursor: pointer;
          }
      `}</style>
      {/*  */}
    </>
  );
}
